import { defineComponent as _defineComponent } from 'vue'
import type { TranslateResult } from 'vue-i18n';
import type Provider from '@/interfaces/provider';
import type { TopArticleFragment } from '@/pages/graphql/queries/GetTopArticles.query';
import type { TopTitleFragment, TopTitle_Season_Fragment } from '@/pages/graphql/queries/GetTopTitles.query';

import ConsentToggleButton from '@/components/consent/ConsentToggleButton.vue';

import { COUNTRY_WITHOUT_TITLEDETAIL } from '@/constants/web-locales.constant';
import { CollectionType } from '@/enums/collection-type';

import { SnowplowTitleContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getArticleSlug } from '@/features/editorial/helper';

import { storeToRefs } from 'pinia';
import { useFooterStore } from '@/stores/pinia/useFooterStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';

import { useI18n } from '@/helpers/composables/useI18n';
import { useRoute, useRouter } from 'vue-router/composables';

import { computed, ref } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AppDefaultFooter',
  setup(__props) {

const routesWithTitleList = [
	CollectionType.NEW,
	CollectionType.POPULAR,
	CollectionType.SEARCH,
	CollectionType.MY_LISTS,
	CollectionType.PUBLIC_LISTS,
	CollectionType.TV_SHOW_TRACKING,
];

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { providers } = useConstantStore();
const { setFilterValue } = useFilterStore();
const languageStore = useLanguageStore();
const { topMovies, topShows, topUpcomingMovies, topUpcomingShows, topArticles } = storeToRefs(useFooterStore());

const providersLimit = ref(5);
const currentYear = new Date().getFullYear();

const version = computed(() => process.env.VERSION);

const showAppFooter = computed(() => {
	const currentCollection: CollectionType = route.meta?.collectionType;
	return !routesWithTitleList.includes(currentCollection);
});

const latestEditorialHeader = computed(() => t('WEBAPP_FOOTER_TOP_X_NEWS', { x: topArticles.value.length }));

const shouldLinkToUpcoming = computed(() => router.getRoutes().some(({ name }) => name === 'app.titles.upcoming.list'));

const titleUrlsEnabled = computed(() => !COUNTRY_WITHOUT_TITLEDETAIL.includes(languageStore.webLocale));

function getShowTitle(title: TopTitleFragment) {
	return [
		title.objectType === 'SHOW_SEASON' ? (title as TopTitle_Season_Fragment).show.content.title : '',
		title.content.title,
	].join(' ');
}

function handleTopProvider(provider: Provider, collectionType: CollectionType, index: number) {
	const translatable =
		collectionType === CollectionType.POPULAR
			? t('WEBAPP_FOOTER_TOP_X_PROVIDERS', { x: providersLimit.value })
			: t('WEBAPP_FOOTER_TOP_X_NEW_ON_PROVIDER', { x: providersLimit.value });

	trackProviderClick(provider, translatable, index);
	setProvider(provider, collectionType);
}

function trackHeadingClick(headline: TranslateResult) {
	TrackingHelper.trackEvent('userinteraction', {
		action: 'tabchange',
		label: `${headline}`,
		property: 'footer',
		useLastRoute: true,
	});
}

function trackProviderClick(provider: Provider, headline: TranslateResult, value: number) {
	TrackingHelper.trackEvent('userinteraction', {
		action: 'tabchange',
		label: `${headline},${provider.packageId}`,
		value,
		property: 'footer',
		useLastRoute: true,
	});
}

function trackClick(item: TopTitleFragment | TopArticleFragment, headline: TranslateResult, value: number) {
	const contexts = [];

	if (item.__typename !== 'Article') {
		contexts.push(new SnowplowTitleContext(item.objectId, item.objectType));
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: `${headline}`,
			value,
			property: 'footer',
			useLastRoute: true,
		},
		contexts
	);
}

function setProvider(provider: Provider, collectionType: CollectionType) {
	setFilterValue({
		filterName: 'providers',
		filterValue: [provider.shortName],
		collectionType,
		// prevents duplicate navigation
		updateRouteState: false,
	});
}

return { __sfc: true,routesWithTitleList, route, router, t, providers, setFilterValue, languageStore, topMovies, topShows, topUpcomingMovies, topUpcomingShows, topArticles, providersLimit, currentYear, version, showAppFooter, latestEditorialHeader, shouldLinkToUpcoming, titleUrlsEnabled, getShowTitle, handleTopProvider, trackHeadingClick, trackProviderClick, trackClick, setProvider, ConsentToggleButton, CollectionType, getArticleSlug }
}

})